import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * Builds one condition in the shape ConversationFilter's v-model expects.
 * @param {string} attributeKey
 * @param {string} filterOperator
 * @param {Array|Object|string} values
 * @returns {Object}
 */
const condition = (attributeKey, filterOperator, values) => ({
  attributeKey,
  attributeModel: 'standard',
  filterOperator,
  values,
  queryOperator: 'and',
  customAttributeType: '',
});

// The date attributes render a native <input type="date">, so the value has to
// be the YYYY-MM-DD string that input produces.
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Ready-made filters for the cases that would otherwise cost three selects each.
 * A preset only fills the condition rows: applying stays an explicit action, so
 * the person sees what was built before the list changes under them.
 * @returns {{ presets: import('vue').ComputedRef<Array<{key: string, label: string, icon: string, build: () => Array}>> }}
 */
export function useConversationFilterPresets() {
  const { t } = useI18n();
  const currentUser = useMapGetter('getCurrentUser');
  const myTeams = useMapGetter('teams/getMyTeams');

  const priorityOption = id => ({
    id,
    name: t(`CONVERSATION.PRIORITY.OPTIONS.${id.toUpperCase()}`),
  });

  const presets = computed(() => {
    const [myTeam] = myTeams.value ?? [];

    return [
      {
        key: 'unanswered_1h',
        label: t('FILTER.PRESETS.UNANSWERED_1H'),
        icon: 'i-lucide-alarm-clock',
        // waiting_since, not last_activity_at: the latter is bumped by our own
        // replies too, so a conversation we answered and the contact dropped
        // would read as "unanswered". waiting_since is cleared the moment an
        // agent replies and on resolve, so it means exactly "still owed a reply".
        build: () => [condition('waiting_since', 'hours_before', '1')],
      },
      {
        key: 'unanswered',
        label: t('FILTER.PRESETS.UNANSWERED'),
        icon: 'i-lucide-alarm-clock-off',
        // hours_before 24, not days_before 1: the latter counts from midnight, so
        // "a day" drifted between 24 and 48 hours depending on the time of day.
        build: () => [condition('waiting_since', 'hours_before', '24')],
      },
      {
        key: 'unowned_1h',
        label: t('FILTER.PRESETS.UNOWNED_1H'),
        icon: 'i-lucide-user-round-x',
        build: () => [
          condition('assignee_id', 'is_not_present', []),
          condition('waiting_since', 'hours_before', '1'),
        ],
      },
      {
        key: 'my_urgent',
        label: t('FILTER.PRESETS.MY_URGENT'),
        icon: 'i-lucide-zap',
        build: () => [
          condition('assignee_id', 'equal_to', {
            id: currentUser.value.id,
            name: currentUser.value.name,
          }),
          condition('priority', 'equal_to', [priorityOption('urgent')]),
        ],
      },
      {
        key: 'high_priority',
        label: t('FILTER.PRESETS.HIGH_PRIORITY'),
        icon: 'i-lucide-triangle-alert',
        build: () => [
          condition('priority', 'equal_to', [
            priorityOption('urgent'),
            priorityOption('high'),
          ]),
        ],
      },
      {
        key: 'resolved_today',
        label: t('FILTER.PRESETS.RESOLVED_TODAY'),
        icon: 'i-lucide-circle-check',
        build: () => [
          condition('status', 'equal_to', [
            {
              id: 'resolved',
              name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
            },
          ]),
          condition('last_activity_at', 'is_greater_than', today()),
        ],
      },
      ...(myTeam
        ? [
            {
              key: 'my_team',
              label: t('FILTER.PRESETS.MY_TEAM'),
              icon: 'i-lucide-users',
              build: () => [
                condition('team_id', 'equal_to', {
                  id: myTeam.id,
                  name: myTeam.name,
                }),
              ],
            },
          ]
        : []),
      {
        key: 'with_label',
        label: t('FILTER.PRESETS.WITH_LABEL'),
        icon: 'i-lucide-tag',
        build: () => [condition('labels', 'equal_to', [])],
      },
      {
        key: 'with_contact',
        label: t('FILTER.PRESETS.WITH_CONTACT'),
        icon: 'i-lucide-user-round',
        // contact_name, not contact_id: picking one person from a combo answers a
        // different question than "every conversation with an Iris in it".
        build: () => [condition('contact_name', 'contains', '')],
      },
    ];
  });

  return { presets };
}
